'use client';

import { useEffect, useMemo, useRef } from 'react';
import { Info } from 'lucide-react';
import dayjs from 'dayjs';
import * as echarts from 'echarts';
import type { ECharts } from 'echarts';
import { formatCompactValue } from '@/lib/utils/number';
import type { MarketEventSocialSignal } from '@/components/predic/ui/MarketEvent';
import { TranslationParams, useTranslation } from '@/lib/i18n';
import { formatDateTime } from '@/lib/utils/date';
import { cn } from '@/lib/utils';

interface MarketSocailSignalProps {
  socialSignal?: MarketEventSocialSignal;
}

type ChartPoint = {
  observedAt: number;
  value: number;
};

export default function MarketSocailSignal({ socialSignal }: MarketSocailSignalProps) {
  const { t } = useTranslation();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<ECharts | null>(null);
  const points = useMemo(() => getHistoryPoints(socialSignal), [socialSignal]);

  const latestPoint = points[points.length - 1];
  const totalInteractions = latestPoint?.value ?? socialSignal?.total_interactions ?? 0;
  const change24h = points.length === 1 ? totalInteractions : socialSignal?.change_in_24h;

  useEffect(() => {
    if (!chartRef.current || points.length === 0) return;

    const chart = chartInstanceRef.current ?? echarts.init(chartRef.current);
    chartInstanceRef.current = chart;
    chart.setOption(createSocialSignalOption(points, t), true);

    const resizeObserver = new ResizeObserver(() => chart.resize());
    resizeObserver.observe(chartRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [points, t]);

  useEffect(() => {
    return () => {
      chartInstanceRef.current?.dispose();
      chartInstanceRef.current = null;
    };
  }, []);

  if (!socialSignal) return null;

  return (
    <section className="mt-2">
      <h2 className="py-3 text-xl font-semibold leading-6 text-white">{t('Social Signal')}</h2>
      <div className="mt-2 grid grid-cols-[164px_minmax(0,1fr)] gap-7.5">
        <aside className="flex flex-col">
          <div className="text-[14px] font-semibold leading-4 text-[#A7ABB2]">
            {t('Social Engagement')}
          </div>
          <div className="mt-3 text-[32px] font-semibold leading-10 text-white">
            {totalInteractions === 0 ? '-' : formatCompactValue(totalInteractions)}
          </div>
          <div
            className={cn('text-sm font-medium', {
              'text-[#FF4D4F]': change24h && change24h < 0,
              'text-[#757575]': change24h === 0,
              'text-[#14C784]': change24h && change24h > 0,
            })}
          >
            {change24h ? formatCompactValue(change24h) : '-'} {t('(24h)')}
          </div>
          <div className="mt-auto mb-3 h-px w-full bg-[#252525]" />
          <div className="gap-2 text-[12px] leading-5 text-[#B7B7B7]">
            <Info className="mr-1 text-[#B7B7B7] inline-flex" size={13} />
            <span>{t('Total interactions including Likes, Retweets, Replies and Quotes')}</span>
          </div>
        </aside>

        <div className="relative h-45 min-w-0">
          {points.length > 0 ? (
            <div ref={chartRef} className="h-full w-full" />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#757575]">
              {t('No social signal history yet')}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function getHistoryPoints(socialSignal?: MarketEventSocialSignal): ChartPoint[] {
  const raw =
    socialSignal?.history
      ?.map((item) => {
        const observedAt = new Date(item.observed_at).getTime();
        const value = Number(item.total_interactions);
        return { observedAt, value };
      })
      .filter((item) => Number.isFinite(item.observedAt) && Number.isFinite(item.value))
      .sort((a, b) => a.observedAt - b.observedAt) ?? [];

  if (raw.length <= 1) return raw;

  return interpolateToHourlyPoints(raw);
}

/**
 * 将稀疏的采样点线性插值为整点小时粒度的 24h 曲线。
 * - 两个采样点之间的空缺小时用线性插值填充
 * - 第一个采样点之前、最后一个采样点之后不外推
 */
function interpolateToHourlyPoints(raw: ChartPoint[]): ChartPoint[] {
  const ONE_HOUR = 3_600_000;
  const firstHour = Math.ceil(raw[0].observedAt / ONE_HOUR) * ONE_HOUR;
  const lastHour = Math.floor(raw[raw.length - 1].observedAt / ONE_HOUR) * ONE_HOUR;

  if (firstHour > lastHour) return raw;

  const result: ChartPoint[] = [];
  let rawIdx = 0;

  for (let ts = firstHour; ts <= lastHour; ts += ONE_HOUR) {
    // 移动 rawIdx 使 raw[rawIdx+1] 是 ts 右侧最近的点
    while (rawIdx < raw.length - 2 && raw[rawIdx + 1].observedAt < ts) {
      rawIdx++;
    }

    const left = raw[rawIdx];
    const right = raw[rawIdx + 1];

    if (ts <= left.observedAt) {
      result.push({ observedAt: ts, value: left.value });
    } else if (ts >= right.observedAt) {
      result.push({ observedAt: ts, value: right.value });
    } else {
      const ratio = (ts - left.observedAt) / (right.observedAt - left.observedAt);
      const value = Math.round(left.value + ratio * (right.value - left.value));
      result.push({ observedAt: ts, value });
    }
  }

  return result;
}

function createSocialSignalOption(
  points: ChartPoint[],
  t: (key: string, params?: TranslationParams | undefined) => string,
): echarts.EChartsOption {
  return {
    backgroundColor: 'transparent',
    animation: false,
    grid: {
      top: 0,
      right: 35,
      bottom: 24,
      left: 4,
      containLabel: false,
    },
    tooltip: {
      trigger: 'axis',
      confine: true,
      borderWidth: 0,
      backgroundColor: '#1D1D1D',
      padding: [17, 18],
      extraCssText: 'width:176px;border-radius:10px;box-shadow:none;',
      axisPointer: {
        type: 'none',
      },
      formatter: (params) => {
        const param = Array.isArray(params) ? params[0] : params;
        const value = Array.isArray(param.value) ? Number(param.value[1]) : 0;
        const observedAt = Array.isArray(param.value) ? Number(param.value[0]) : Date.now();

        return [
          `<div style="font-size:12px;font-weight:500;line-height:14px;color:#777;">${formatDateTime(
            observedAt,
            'MMM D, h:mm A',
          )}</div>`,
          `<div style="margin-top:14px;font-size:14px;line-height:16px;color:#fff;">${t('Social Engagement')}</div>`,
          `<div style="margin-top:7px;font-size:16px;font-weight:700;line-height:18px;color:#fff;">${formatCompactValue(
            value,
          )}</div>`,
        ].join('');
      },
    },
    xAxis: {
      type: 'time',
      boundaryGap: ['0%', '0%'],
      axisLine: {
        show: true,
        lineStyle: {
          color: '#303030',
          width: 1,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: '#757575',
        fontSize: 10,
        // formatter: (value: number) => dayjs(value).format('M/D'),
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: '#2D2D2D',
          width: 1,
          type: 'dashed',
          dashOffset: 0,
        },
      },
    },
    yAxis: {
      type: 'value',
      position: 'right',
      min: 0,
      axisLine: {
        show: true,
        lineStyle: {
          color: '#303030',
          width: 1,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: '#757575',
        fontSize: 10,
        formatter: (value: number) => formatCompactValue(value),
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: '#2D2D2D',
          width: 1,
          type: 'dashed',
        },
      },
    },
    series: [
      {
        type: 'line',
        data: points.map((point) => [point.observedAt, point.value]),
        smooth: true,
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 10,
        emphasis: {
          scale: false,
          itemStyle: {
            color: '#00D18B',
            borderColor: '#00D18B',
            borderWidth: 0,
          },
        },
        lineStyle: {
          color: '#00D18B',
          width: 2.5,
        },
        itemStyle: {
          color: '#00D18B',
        },
      },
    ],
  };
}
